/**
 * "AI" tab of the runtime System administration console — where a functional
 * administrator configures the AI that answers the end-user Assistant FOR THEIR
 * OWN ORGANIZATION.
 *
 * Why an organization configures its own: the project owner's Configure AI
 * profile is the BUILDER's personal AI account (their subscription / API key).
 * It serves the owner's own organization and the builder's preview only — every
 * other organization running the app supplies its own credential here, so no
 * one's AI spend or account leaks across a tenant boundary. Until this is set,
 * the Assistant tells that organization's users exactly what is missing.
 *
 * The credential is stored Fernet-encrypted server-side and is never sent back:
 * the form shows whether one is stored, and leaving the field blank keeps it.
 *
 * Every organization gets a second choice: **AI Credit**, which runs the
 * assistant on Twasta's own platform key and charges it to THAT organization's
 * AI credits — no provider account to open, no key to paste. "That
 * organization" is the one whose func-admin is standing at this form, not the
 * application's owner: a tenant running somebody else's app spends its own
 * credits and can never reach the owner's wallet. The backend decides what is
 * on offer (`fundings` on the GET response); this form only renders what it
 * was told is available.
 *
 * The owner's OWN organization sees a banner saying what serves it today. It is
 * not a fixed sentence: the server resolves the same ladder the assistant runs
 * (`runtime_ai_service.owner_fallback`) and sends back `owner_fallback`, so the
 * banner names the profile that answers — and, when nothing does, says so in
 * red instead of promising a Configure AI profile that is not there while the
 * assistant refuses that organization's users. AI Credit is not part of that
 * ladder: it answers only where the row below chose it as its funding, so
 * removing the row stops the credit spend rather than continuing it silently.
 *
 * The provider and model pickers are driven by the backend LLM catalog
 * (`catalog` / `agent_models` on the GET response) — the same list the builder's
 * own Settings → AI page offers — so an admin picks a current model instead of
 * typing an id from memory. A model id outside the catalog is still allowed
 * through the "Custom model ID" option, because a provider can ship a model
 * before the catalog catches up. "Test" then proves the choice: it makes a real
 * one-token call to THAT model with the entered (or stored) key.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, Loader2, Save, Trash2, XCircle, Zap } from 'lucide-react'
import {
  getRuntimeAiConfig, saveRuntimeAiConfig, deleteRuntimeAiConfig, testRuntimeAiConfig,
  testRuntimeEmbedding,
  type RuntimeAiConfigView, type RuntimeAiEffort, type RuntimeAiFunding, type RuntimeAiTaskPick,
  type RuntimeAiModelOption, type RuntimeAiTestResult,
  type RuntimeEmbeddingSource, type RuntimeEmbeddingTestResult,
  type RuntimeImageUpdate,
} from '@/api/runtimeAi'
import { RuntimeImageSection, imageConfigError } from './RuntimeImageSection'
import { PROVIDERS as LOCAL_PROVIDERS, modelsForAgent, type AgentId } from '@/data/llmCatalog'

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: 14, background: '#fff', color: '#1f2937', outline: 'none', width: '100%',
}
const btn = (primary?: boolean): React.CSSProperties => ({
  padding: '7px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  border: primary ? 'none' : '1px solid #cbd5e1',
  background: primary ? '#4f46e5' : '#fff', color: primary ? '#fff' : '#334155',
  display: 'inline-flex', alignItems: 'center', gap: 6,
})

const ENGINE_LABEL: Record<string, string> = {
  llm: 'Built-in LLM (provider API key)',
  claude_code: 'Claude Code agent',
  codex: 'Codex agent',
  opencode: 'OpenCode agent',
}

const FUNDING_LABEL: Record<string, string> = {
  own_key: 'Our own provider API key',
  platform: 'AI Credit (charged to your credit balance)',
}

/**
 * Reasoning effort, said in terms of what the admin is trading away. The
 * default is "None" because it is the only setting every model accepts: a
 * reasoning model that is never told otherwise thinks by default, and OpenAI
 * then refuses the assistant's tools outright. Anything above None costs more
 * tokens and more seconds per answer, which is the whole decision.
 */
const EFFORT_LABEL: Record<string, string> = {
  none: 'None — answer directly (default)',
  low: 'Low — a little thinking',
  medium: 'Medium — balanced',
  high: 'High — think hard',
  xhigh: 'Extra high — think hardest',
}
const EFFORT_NOTE: Record<string, string> = {
  none: 'The assistant answers without a reasoning pass. Fastest and cheapest, and the only setting every model accepts.',
  low: 'A short reasoning pass before answering. Slower and dearer than None; better on multi-step questions.',
  medium: 'A balanced reasoning pass. Noticeably slower and dearer per answer.',
  high: 'A long reasoning pass. Best on hard questions, and the slowest and dearest per answer.',
  xhigh: 'The longest reasoning pass the model offers. Only worth it for genuinely hard questions — every answer pays for it.',
}

/**
 * Where document text is converted into vectors, said as the decision it is.
 *
 * "Nothing chosen" is not "off": documents are still filed and still found by
 * keyword — they are simply no longer found by MEANING, and the conversion,
 * where it happens at all, runs on the application author's account.
 */
const EMBEDDING_SOURCE_LABEL: Record<string, string> = {
  '': "The application owner's provider (default)",
  own_key: 'Our own embedding provider',
  platform: 'AI Credit (charged to your credit balance)',
}

const PROVIDER_LABEL: Record<string, string> = {
  openai: 'OpenAI',
  google: 'Google',
}

/**
 * Why this embedding profile could not embed, or '' if it can.
 *
 * A mirror of `runtime_ai_service.embedding_config_error`, kept in step by hand
 * for the same reason `ownKeyConfigError` is: the server is the authority, and
 * this exists so the reason appears on the form rather than arriving as a 400.
 *
 * It matters more here than it does above, because a half-filled embedding
 * profile fails SILENTLY — no error, no outage, just steadily worse answers
 * from a repository nobody thinks to suspect.
 */
function embeddingConfigError(
  source: RuntimeEmbeddingSource, provider: string, apiKey: string,
  hasStoredKey: boolean, baseUrl: string, platformAvailable: boolean,
): string {
  if (!source) return ''
  if (source === 'platform') {
    return platformAvailable ? ''
      : 'AI Credit cannot embed on this instance: its platform AI is not an '
        + 'embedding-capable provider. Choose your own OpenAI or Google key instead, '
        + 'or ask an instance administrator to set a platform key on one of those providers.'
  }
  if (!provider.trim()) {
    return 'Choose an embedding provider — only OpenAI and Google publish an '
      + 'embeddings endpoint, so there is nothing to call until one is selected.'
  }
  // A self-hosted endpoint usually has no credential, and demanding one would
  // put the whole on-premise path out of reach.
  if (baseUrl.trim()) return ''
  if (!apiKey.trim() && !hasStoredKey) {
    return "Add the embedding provider's API key — without a credential (or a "
      + 'self-hosted endpoint) there is nothing to embed your documents with, and '
      + 'they would be searched by keyword only.'
  }
  return ''
}

/**
 * Why this OWN-KEY configuration could not answer, or '' if it can.
 *
 * A mirror of `runtime_ai_service.own_key_config_error`, kept in step by hand
 * because the two languages cannot share it. The SERVER is the authority —
 * this exists so the reason appears on the form instead of arriving as a 400
 * after a round trip, and so Save stops being a greyed-out button with nothing
 * to say. A half-filled row is not obviously broken: it saves, the console
 * reports "not configured yet", and the people who find out are that
 * organization's end users.
 *
 * The built-in LLM engine and OpenCode are checked (`opencode_config_error`
 * on the server). Claude Code / Codex run keyless on the host's own
 * subscription and derive their provider from the engine.
 */
function ownKeyConfigError(
  engine: string, provider: string, model: string,
  apiKey: string, hasStoredKey: boolean, freeModel = false,
): string {
  if (engine === 'opencode') {
    if (!model.includes('/')) {
      return 'Choose an OpenCode model — OpenCode runs a provider/model id '
        + '(for example anthropic/claude-sonnet-4-5).'
    }
    if (apiKey.trim() || hasStoredKey || freeModel) return ''
    return `Enter the API key for ${model.split('/')[0]} — ${model} is not a free OpenCode `
      + 'model, and without a key it would run on this server\'s own OpenCode account. '
      + 'Choose a free model, add your key, or choose AI Credit.'
  }
  if (engine !== 'llm') return ''
  if (!provider.trim()) {
    return 'Choose a provider — the built-in LLM engine calls that provider\'s API '
      + 'directly, so there is nothing to call until one is selected.'
  }
  if (!model.trim()) {
    return 'Choose a model — the Assistant sends an explicit model id on every call, '
      + 'and a blank one cannot answer.'
  }
  if (!apiKey.trim() && !hasStoredKey) {
    return 'Enter an API key — the built-in LLM engine has no credential to call the '
      + 'provider with. Paste the key, or choose AI Credit to run on Twasta\'s own '
      + 'key instead.'
  }
  return ''
}

/** Sentinel option that swaps the model dropdown for a free-text box. */
const CUSTOM = '__custom__'

const isAgentEngine = (engine: string) =>
  engine === 'claude_code' || engine === 'codex' || engine === 'opencode'

/**
 * The models offered for the current engine/provider. Prefers what the backend
 * catalog reported (so a newly-catalogued model appears without a frontend
 * release) and falls back to the bundled mirror when talking to an older
 * backend that doesn't send one.
 */
function modelOptionsFor(
  view: RuntimeAiConfigView | null, engine: string, provider: string,
): RuntimeAiModelOption[] {
  if (engine === 'opencode') {
    // Live from the CLI on the host — there is no bundled list to fall back on.
    return (view?.agent_models?.opencode || []).map((m) => (
      { ...m, label: m.free ? `${m.label} — free` : m.label }))
  }
  if (isAgentEngine(engine)) {
    const fromServer = view?.agent_models?.[engine]
    if (fromServer?.length) return fromServer
    return modelsForAgent(engine as AgentId).map((m) => ({ id: m.id, label: m.label }))
  }
  if (!provider) return []
  const fromServer = view?.catalog?.find((p) => p.id === provider)
  if (fromServer?.models?.length) return fromServer.models
  const local = LOCAL_PROVIDERS.find((p) => p.id === provider)
  return (local?.models || []).map((m) => ({ id: m.id, label: m.label }))
}

/** Provider list for the dropdown: catalog labels when available, ids otherwise. */
function providerOptionsFor(view: RuntimeAiConfigView | null): { id: string; label: string }[] {
  if (view?.catalog?.length) return view.catalog.map((p) => ({ id: p.id, label: p.label }))
  const allowed = view?.providers
  const local = LOCAL_PROVIDERS
    .filter((p) => !allowed || allowed.includes(p.id))
    .map((p) => ({ id: p.id, label: p.label }))
  if (local.length) return local
  return (allowed || []).map((p) => ({ id: p, label: p }))
}

export function RuntimeAiPanel({ projectId, ownerId }: { projectId: string; ownerId: string }) {
  const [view, setView] = useState<RuntimeAiConfigView | null>(null)
  const [engine, setEngine] = useState('llm')
  const [funding, setFunding] = useState<RuntimeAiFunding>('platform')
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [effort, setEffort] = useState<RuntimeAiEffort>('none')
  // Advanced: model / effort per kind of work ({} = everything on the main setting).
  const [taskPicks, setTaskPicks] = useState<Record<string, RuntimeAiTaskPick>>({})
  // Sticky only while the admin is typing a model the catalog doesn't list —
  // a model that IS listed selects itself in the dropdown on its own.
  const [customModel, setCustomModel] = useState(false)
  const [apiKey, setApiKey] = useState('')
  // Document search. A second credential with its own Test, because a broken
  // one produces no error anybody sees — only answers that quietly get worse.
  const [embSource, setEmbSource] = useState<RuntimeEmbeddingSource>('')
  const [embProvider, setEmbProvider] = useState('')
  const [embModel, setEmbModel] = useState('')
  const [embBaseUrl, setEmbBaseUrl] = useState('')
  const [embKey, setEmbKey] = useState('')
  // Carried, never edited here: a repository indexed at 768 dimensions must
  // keep being queried at 768, and saving the form is no reason to forget a
  // width somebody pinned deliberately.
  const [embDims, setEmbDims] = useState(0)
  // Image generation — rides this form's Save; its section owns its Test.
  const [img, setImg] = useState<RuntimeImageUpdate>({ source: '' })
  const [embTesting, setEmbTesting] = useState(false)
  const [embTest, setEmbTest] = useState<RuntimeEmbeddingTestResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [test, setTest] = useState<RuntimeAiTestResult | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true); setErr(null); setTest(null)
    getRuntimeAiConfig(ownerId, projectId)
      .then((v) => {
        setView(v)
        setEngine(v.engine || 'llm')
        setFunding(v.funding || 'platform')
        setProvider(v.provider || '')
        setModel(v.model || '')
        setEffort(v.effort || 'none')
        setTaskPicks(v.task_config || {})
        setCustomModel(false)
        setApiKey('')
        setEmbSource((v.embedding?.source || '') as RuntimeEmbeddingSource)
        setEmbProvider(v.embedding?.provider || '')
        setEmbModel(v.embedding?.model || '')
        setEmbBaseUrl(v.embedding?.base_url || '')
        setEmbDims(v.embedding?.dimensions || 0)
        setEmbKey('')
        setEmbTest(null)
        setImg({ source: (v.image?.source || '') as RuntimeImageUpdate['source'],
                 provider: v.image?.provider || '', model: v.image?.model || '' })
      })
      .catch((e) => setErr(errText(e) || 'Failed to load the AI configuration'))
      .finally(() => setLoading(false))
  }, [ownerId, projectId])
  useEffect(() => { load() }, [load])

  const isAgent = isAgentEngine(engine)
  const embPlatformAvailable = view?.embedding_platform_available !== false
  const embDefaults = view?.embedding_models || {}
  const embDefaultModel = embDefaults[embProvider]?.model || ''
  const embDefaultDims = embDefaults[embProvider]?.dimensions || 0
  const embIncomplete = embeddingConfigError(
    embSource, embProvider, embKey, !!view?.embedding?.credential_configured,
    embBaseUrl, embPlatformAvailable)
  const embProviderOptions = view?.embedding_providers || ['openai', 'google']
  // Recomputed as the admin types, so the message tracks the form rather than
  // the last failed save.
  const freeModel = engine === 'opencode'
    && !!view?.agent_models?.opencode?.some((m) => m.id === model && m.free)
  const incomplete = funding === 'platform' ? '' : ownKeyConfigError(
    engine, provider, model, apiKey, !!view?.credential_configured, freeModel)
  // AI Credit supplies provider, model and key from the platform default, so
  // the whole credential half of the form is not just disabled — it is absent.
  const onCredit = funding === 'platform'
  const fundingOptions = view?.fundings || ['own_key']
  const providerOptions = useMemo(() => providerOptionsFor(view), [view])
  const modelOptions = useMemo(
    () => modelOptionsFor(view, engine, provider), [view, engine, provider])

  // A stored model that the catalog doesn't list still has to be editable, so
  // it falls into the custom box rather than silently disappearing.
  const modelInCatalog = modelOptions.some((m) => m.id === model)
  const showCustomModel = customModel || (!!model && !modelInCatalog)

  /** Keep a model only while it still belongs to the new engine/provider. */
  const retargetModel = (nextEngine: string, nextProvider: string) => {
    const next = modelOptionsFor(view, nextEngine, nextProvider)
    setModel((m) => (m && next.some((o) => o.id === m) ? m : ''))
    keepTaskModels(next.map((o) => o.id))
    setCustomModel(false)
    setTest(null)
  }

  /**
   * Switching funding switches which models are even callable: AI Credit runs
   * the instance's own credential, so a model chosen for this organization's
   * provider key cannot survive the move (and would be refused on save).
   * Dropping it back to the platform default is the honest outcome.
   */
  const retargetFunding = (next: RuntimeAiFunding) => {
    setFunding(next)
    setTest(null)
    setCustomModel(false)
    const allowed = next === 'platform'
      ? (view?.platform_ai?.models || []).map((m) => m.id)
      : modelOptionsFor(view, engine, provider).map((m) => m.id)
    setModel((m) => (m && allowed.includes(m) ? m : ''))
    keepTaskModels(allowed)
  }

  /** A task keeps its model only while the new configuration can run it — a
   *  task never changes the credential, so its model must belong to it. */
  function keepTaskModels(allowed: string[]) {
    setTaskPicks((cur) => {
      const next: Record<string, RuntimeAiTaskPick> = {}
      for (const [id, p] of Object.entries(cur)) {
        const keepModel = !!p.model && allowed.includes(p.model)
        const pick: RuntimeAiTaskPick = {
          ...(keepModel ? { model: p.model } : {}), ...(p.effort ? { effort: p.effort } : {}),
        }
        if (pick.model || pick.effort) next[id] = pick
      }
      return next
    })
  }

  const setTaskPick = (id: string, patch: RuntimeAiTaskPick) => {
    setTaskPicks((cur) => {
      const pick = { ...(cur[id] || {}), ...patch }
      const clean: RuntimeAiTaskPick = {
        ...(pick.model ? { model: pick.model } : {}), ...(pick.effort ? { effort: pick.effort } : {}),
      }
      const next = { ...cur }
      if (clean.model || clean.effort) next[id] = clean
      else delete next[id]
      return next
    })
  }

  const save = async () => {
    const imgIncomplete = imageConfigError(img, view)
    if (imgIncomplete) {
      setErr(imgIncomplete)
      toast.error(imgIncomplete)
      return
    }
    if (embIncomplete) {
      // Refused on the form for the same reason the endpoint refuses it: this
      // profile saves happily and then degrades every document search in the
      // organization, with nothing to see but worse answers.
      setErr(embIncomplete)
      toast.error(embIncomplete)
      return
    }
    if (incomplete) {
      // Not sent at all. The endpoint refuses the same configuration, so the
      // round trip would only be a slower way to show this sentence.
      setErr(incomplete)
      toast.error(incomplete)
      return
    }
    setSaving(true); setErr(null)
    try {
      const v = await saveRuntimeAiConfig(ownerId, projectId, {
        engine, funding, provider, model, effort,
        task_config: taskPicks,
        // Blank means "keep what's stored" — never round-trip a secret.
        api_key: apiKey.trim() ? apiKey.trim() : undefined,
        embedding: {
          source: embSource,
          provider: embSource === 'own_key' ? embProvider : '',
          model: embModel,
          base_url: embSource === 'own_key' ? embBaseUrl : '',
          dimensions: embDims,
          api_key: embKey.trim() ? embKey.trim() : undefined,
        },
        image: {
          source: img.source,
          provider: img.source === 'own_key' ? img.provider : '',
          model: img.model || '',
          api_key: img.source === 'own_key' && (img.api_key || '').trim()
            ? (img.api_key || '').trim() : undefined,
        },
      })
      setView((prev) => ({ ...(prev as RuntimeAiConfigView), ...v }))
      setApiKey('')
      setEmbKey('')
      setImg((cur) => ({ ...cur, api_key: '' }))
      toast.success('Assistant AI configuration saved')
    } catch (e) {
      const msg = errText(e) || 'Failed to save'
      setErr(msg); toast.error(msg)
    } finally { setSaving(false) }
  }

  const runTest = async () => {
    setTesting(true); setTest(null); setErr(null)
    try {
      const r = await testRuntimeAiConfig(ownerId, projectId, {
        engine, funding, provider, model, effort,
        api_key: apiKey.trim() ? apiKey.trim() : undefined,
      })
      setTest(r)
      if (r.ok) toast.success('AI configuration verified')
    } catch (e) {
      setTest({ ok: false, error: errText(e) || 'The test could not be run' })
    } finally { setTesting(false) }
  }

  const runEmbeddingTest = async () => {
    setEmbTesting(true); setEmbTest(null)
    try {
      const r = await testRuntimeEmbedding(ownerId, projectId, {
        source: embSource,
        provider: embSource === 'own_key' ? embProvider : '',
        model: embModel,
        base_url: embSource === 'own_key' ? embBaseUrl : '',
        dimensions: embDims,
        api_key: embKey.trim() ? embKey.trim() : undefined,
      })
      setEmbTest(r)
      if (r.ok) toast.success('Embedding provider verified')
    } catch (e) {
      setEmbTest({ ok: false, error: errText(e) || 'The test could not be run' })
    } finally { setEmbTesting(false) }
  }

  const remove = async () => {
    // Say what removal actually leaves behind. The old text promised the
    // assistant would stop, which was untrue for an owner-tenant app riding
    // its Configure AI profile — and the one thing it has to be right about
    // is an admin removing a row precisely to stop the spending.
    const left = view?.is_owner_tenant && view?.owner_fallback?.available === true
      ? `The assistant will fall back to ${view.owner_fallback.label
          ? `the AI profile chosen in Configure AI (${view.owner_fallback.label})`
          : 'the AI profile chosen in Configure AI'}.`
      : 'The assistant will stop answering for your organization until a new one is set.'
    if (!window.confirm(`Remove this AI configuration? ${left}`)) return
    setSaving(true)
    try { await deleteRuntimeAiConfig(ownerId, projectId); toast.success('Configuration removed'); load() }
    catch (e) { toast.error(errText(e) || 'Failed to remove') }
    finally { setSaving(false) }
  }

  if (loading) return <div style={{ padding: 26, textAlign: 'center', color: '#64748b' }}><Loader2 className="animate-spin" /></div>
  if (err && !view) return <div style={{ fontSize: 14, color: '#dc2626' }}>{err}</div>

  // What the assistant falls back on with no row here — resolved server-side
  // through the SAME ladder the assistant runs, so this tab and the assistant
  // popup can never tell an administrator two different stories.
  const ownerFallback = view?.owner_fallback
  // `available: null` means the server could not resolve it — say nothing
  // either way rather than claiming an AI that may not be there.
  const fallbackAvailable = ownerFallback?.available !== false
  // 'credit' is no longer a fallback the server resolves — AI Credit answers
  // only where a saved row chose it — but an older payload may still say it.
  const fallbackName = ownerFallback?.source === 'credit'
    ? <>{ownerFallback.label || 'AI Credit'}, charged to your credit balance</>
    : ownerFallback?.label
      ? <>the AI profile chosen in <b>Configure AI</b> ({ownerFallback.label})</>
      : <>the AI profile chosen in <b>Configure AI</b></>
  // A row of this organization's own is what serves; without one the fallback is.
  const ownerServes = !!view?.configured

  // What AI Credit answers with when this organization chooses nothing. Named
  // rather than left as the words "platform default", which tell a person
  // nothing about what their credits are being spent on.
  const platformDefaultModel = view?.platform_ai?.default_model || ''
  const platformProviderLabel = (() => {
    const p = view?.platform_ai
    if (!p) return ''
    if (p.engine === 'claude_code') return 'Claude Code agent'
    if (p.engine === 'codex') return 'Codex agent'
    if (p.engine === 'opencode') return 'OpenCode agent'
    return view?.catalog?.find((c) => c.id === p.provider)?.label || p.provider || ''
  })()

  // Nothing to ping with: an LLM engine needs a provider, and every engine
  // needs either a typed key or one already stored.
  const hasCredential = !!apiKey.trim() || !!view?.credential_configured
  // On AI Credit there is nothing for the admin to supply, so Test is always
  // meaningful: it proves the platform key answers AND that credits remain.
  const canTest = onCredit ? true : (isAgent ? true : (!!provider && hasCredential))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: '#1f2937' }}>
      <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
        The AI that answers the assistant for <b>your organization</b>. It is used only by your
        organization's users — the application owner's own AI account is never charged for, or exposed to, your queries.
      </p>

      {view?.is_owner_tenant && (
        // The banner used to promise the Configure AI profile unconditionally.
        // When no such profile exists (and no AI Credit stands behind it) that
        // promise is the opposite of the truth: the assistant is telling this
        // organization's users "no AI profile is configured for the Assistant",
        // while this tab assures the one person who can fix it that all is
        // well. So it says which of the three states is actually the case.
        ownerServes ? (
          <div style={{ fontSize: 13.5, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8, padding: '9px 11px' }}>
            This application belongs to your organization, and the configuration below is what
            answers the assistant for your users — it overrides <b>Configure AI</b>.{' '}
            {fallbackAvailable
              ? <>Remove it and the assistant falls back to {fallbackName}.</>
              : <>Remove it and the assistant has nothing to fall back on.</>}
          </div>
        ) : fallbackAvailable ? (
          <div style={{ fontSize: 13.5, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8, padding: '9px 11px' }}>
            This application belongs to your organization, so the assistant already runs on
            {' '}{fallbackName}. Anything set here overrides that for your organization's users.
          </div>
        ) : (
          <div style={{ fontSize: 13.5, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px' }}>
            This application belongs to your organization, but there is <b>no AI for the assistant
            to fall back on</b> — it cannot answer your users until one is set.
            {ownerFallback?.message ? ` ${ownerFallback.message}` : ''}
            {' '}Configure one below, or choose an AI profile in <b>Configure AI</b>.
          </div>
        )
      )}

      {!view?.is_owner_tenant && !view?.configured && (
        <div style={{ fontSize: 13.5, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, padding: '9px 11px' }}>
          Not configured yet — the assistant cannot answer your organization's users until you add a credential below.
        </div>
      )}

      {fundingOptions.length > 1 && (
        <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
          Funding
          <select style={inp} value={funding}
            onChange={(e) => retargetFunding(e.target.value as RuntimeAiFunding)}>
            {fundingOptions.map((f) => (
              <option key={f} value={f}>{FUNDING_LABEL[f] || f}</option>
            ))}
          </select>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            {onCredit
              ? "Runs on Twasta's AI, charged to YOUR organization's credit balance — not the application owner's — with nothing to configure and no provider account needed. Usage appears in Billing & Credits."
              : 'Runs on the provider account you supply below. Your provider bills you directly and no credits are used.'}
          </span>
        </label>
      )}
      {/* AI Credit: the provider and the key are the instance's, so neither is
          offered — but WHICH MODEL answers is this organization's decision,
          because it is this organization's credits being spent on it. */}
      {onCredit && view?.platform_key_available !== false && (
        <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
          Model
          <select style={inp} value={model}
            onChange={(e) => { setModel(e.target.value); setTest(null) }}>
            <option value="">
              {platformDefaultModel
                ? `Platform default (${platformDefaultModel})`
                : 'Platform default'}
            </option>
            {(view?.platform_ai?.models || []).map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            {platformProviderLabel
              ? <>Models your instance's platform AI can run ({platformProviderLabel}). A bigger
                  model answers better and costs more credits per question.</>
              : <>The model your instance's platform AI answers with.</>}
          </span>
        </label>
      )}

      {onCredit && view?.platform_key_available === false && (
        <div style={{ fontSize: 13.5, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px' }}>
          This Twasta instance has no platform AI configured, so AI Credit has nothing to
          run on. Ask an instance administrator to set one &mdash; a provider API key, or a
          connected Claude&nbsp;Code / Codex subscription &mdash; or use your own provider key.
        </div>
      )}

      {!onCredit && (
      <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
        Engine
        <select style={inp} value={engine}
          onChange={(e) => { setEngine(e.target.value); retargetModel(e.target.value, provider) }}>
          {(view?.engines || ['llm', 'claude_code', 'codex']).map((e) => (
            <option key={e} value={e}>{ENGINE_LABEL[e] || e}</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {engine === 'opencode'
            ? 'Runs OpenCode where the application is hosted, on the provider/model you pick below — with your key for that provider, or a free OpenCode model with no key. Reads imported PDFs and pictures too.'
            : isAgent
            ? 'Runs the agent CLI where the application is hosted. If that host has no agent CLI installed, the assistant falls back to the built-in LLM using this credential.'
            : 'Calls the provider API directly with the key below.'}
        </span>
      </label>
      )}

      {!onCredit && !isAgent && (
        <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
          Provider
          <select style={inp} value={provider}
            onChange={(e) => { setProvider(e.target.value); retargetModel(engine, e.target.value) }}>
            <option value="">Select a provider…</option>
            {providerOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
      )}

      {!onCredit && (
      <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
        Model {engine === 'opencode'
          ? <span style={{ fontSize: 13, color: '#64748b' }}>(required — a provider/model OpenCode lists on this host)</span>
          : isAgent
          ? <span style={{ fontSize: 13, color: '#64748b' }}>(optional — the agent's default is used when blank)</span>
          : <span style={{ fontSize: 13, color: '#64748b' }}>(required — the Assistant calls this exact model id)</span>}
        <select style={inp} value={showCustomModel ? CUSTOM : model}
          disabled={!isAgent && !provider}
          onChange={(e) => {
            const v = e.target.value
            setTest(null)
            if (v === CUSTOM) { setCustomModel(true); setModel('') }
            else { setCustomModel(false); setModel(v) }
          }}>
          <option value="">
            {engine === 'opencode' ? 'Select a model…'
              : isAgent ? 'Agent default' : provider ? 'Select a model…' : 'Select a provider first…'}
          </option>
          {modelOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          <option value={CUSTOM}>Custom model ID…</option>
        </select>
        {showCustomModel && (
          <input style={inp} value={model} autoFocus
            onChange={(e) => { setModel(e.target.value); setTest(null) }}
            placeholder={engine === 'opencode'
              ? 'provider/model, e.g. anthropic/claude-sonnet-4-5'
              : 'Exact model id, e.g. claude-sonnet-5-5'} />
        )}
        {showCustomModel && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Not in the catalog — use Test below to confirm your account can reach it.
          </span>
        )}
      </label>
      )}

      {!onCredit && (
      <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
        API key
        <input style={inp} type="password" value={apiKey} autoComplete="new-password"
          onChange={(e) => { setApiKey(e.target.value); setTest(null) }}
          placeholder={view?.credential_configured ? '•••••••• stored — type to replace' : 'Paste the API key'} />
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {engine === 'opencode'
            ? `The key for ${model.includes('/') ? model.split('/')[0] : 'the model\'s provider'} — not needed for a free model. `
            : ''}
          Stored encrypted and never shown again. Leave blank to keep the stored key.
        </span>
      </label>
      )}

      {/* Shown on BOTH funding sources: on AI Credit the reasoning tokens are
          charged to this organization's own credits, so how hard the model
          thinks is very much its decision to make. */}
      <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
        Reasoning effort
        <select style={inp} value={effort}
          onChange={(e) => { setEffort(e.target.value as RuntimeAiEffort); setTest(null) }}>
          {(view?.efforts || ['none', 'low', 'medium', 'high', 'xhigh']).map((f) => (
            <option key={f} value={f}>{EFFORT_LABEL[f] || f}</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {EFFORT_NOTE[effort] || ''}
        </span>
      </label>

      {/* ── Advanced: model and effort per kind of work ───────────────────
          Same credential and engine as above for every task — only WHICH
          model of it answers and how hard it thinks. Reading a document
          rarely needs the reasoning a hard question does, and paying for it
          on every import is the usual reason an import feels slow. */}
      {(view?.tasks || []).length > 0 && (
        <details style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}
          open={Object.keys(taskPicks).length > 0}>
          <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Advanced — model and effort per task
            {Object.keys(taskPicks).length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 500, color: '#4f46e5' }}>
                {Object.keys(taskPicks).length} customised
              </span>
            )}
          </summary>
          <p style={{ margin: '8px 0 10px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
            Every kind of AI work below runs on the setting above unless you choose otherwise
            here. A smaller model or less reasoning makes that work faster and cheaper; a
            bigger one makes it more careful. The account and engine never change per task.
          </p>
          <div style={{ display: 'grid', gap: 10 }}>
            {(view?.tasks || []).map((t) => {
              const pick = taskPicks[t.id] || {}
              const opts = onCredit ? (view?.platform_ai?.models || []) : modelOptions
              const mainModel = model || (onCredit ? platformDefaultModel : '')
              const mainModelText = mainModel
                ? (opts.find((o) => o.id === mainModel)?.label || mainModel)
                : (isAgent ? 'agent default' : 'not set')
              return (
                <div key={t.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(140px, 0.8fr)', gap: 8, alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t.label}</div>
                    {t.hint && <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>{t.hint}</div>}
                  </div>
                  <select style={inp} aria-label={`${t.label} model`} value={pick.model || ''}
                    onChange={(e) => setTaskPick(t.id, { model: e.target.value })}>
                    <option value="">Same as above ({mainModelText})</option>
                    {opts.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                  <select style={inp} aria-label={`${t.label} reasoning effort`} value={pick.effort || ''}
                    onChange={(e) => setTaskPick(t.id, { effort: e.target.value as RuntimeAiEffort | '' })}>
                    <option value="">Same as above ({(EFFORT_LABEL[effort] || effort).split(' —')[0]})</option>
                    {(view?.efforts || ['none', 'low', 'medium', 'high', 'xhigh']).map((f) => (
                      <option key={f} value={f}>{(EFFORT_LABEL[f] || f).split(' —')[0]}</option>
                    ))}
                  </select>
                </div>
              )
            })}
          </div>
          {Object.keys(taskPicks).length > 0 && (
            <button type="button" style={{ ...btn(), marginTop: 10 }} onClick={() => setTaskPicks({})}>
              Use the main setting for every task
            </button>
          )}
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>Saved with the rest of this form.</p>
        </details>
      )}

      {/* ── Document search ────────────────────────────────────────────
          A SECOND credential, and the section says why it is separate: only
          OpenAI and Google publish an embeddings endpoint, so an organization
          answering on Anthropic, on an agent CLI or on AI Credit has nothing
          to embed its documents with until it names one here.

          What it does NOT do is widen what a search can reach. The vectors sit
          in this organization's own database beside its own documents; the
          provider is handed one passage at a time and keeps nothing the
          application ever reads back. */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, display: 'grid', gap: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Document search</div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
            Filing a document converts its text into vectors so a question finds the right
            passage by <i>meaning</i>, not just by the words it shares. Only OpenAI and Google
            publish an endpoint for that, so this is a separate provider from the assistant
            above — and an organization on AI&nbsp;Credit or on an agent CLI needs one here.
            The vectors are stored in <b>your organization's own database</b>, beside your own
            documents; no other organization's documents are ever reachable, whatever is
            chosen here.
          </p>
        </div>

        <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
          Embeddings from
          <select style={inp} value={embSource}
            onChange={(e) => {
              const next = e.target.value as RuntimeEmbeddingSource
              setEmbSource(next); setEmbTest(null)
              // The model pin belongs to the provider it was chosen with —
              // carrying it across would send a Google model name to OpenAI,
              // and a width pinned for one model means nothing to another.
              setEmbModel(''); setEmbDims(0)
              if (next === 'own_key' && !embProvider) setEmbProvider(embProviderOptions[0] || 'openai')
            }}>
            <option value="">{EMBEDDING_SOURCE_LABEL['']}</option>
            <option value="own_key">{EMBEDDING_SOURCE_LABEL.own_key}</option>
            <option value="platform" disabled={!embPlatformAvailable}>
              {embPlatformAvailable
                ? EMBEDDING_SOURCE_LABEL.platform
                : `${EMBEDDING_SOURCE_LABEL.platform} — unavailable on this instance`}
            </option>
          </select>
          {!embSource && (
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {view?.embedding_fallback?.available === true
                ? <>Your documents are converted by the application owner's provider
                    ({PROVIDER_LABEL[view.embedding_fallback.provider] || view.embedding_fallback.provider}
                    {view.embedding_fallback.model ? ` · ${view.embedding_fallback.model}` : ''}),
                    on their account. Choose your own above to change that.</>
                : view?.embedding_fallback?.available === false
                  ? <>The application owner has no embedding provider either, so your
                      documents are searched by keyword only — a question finds a passage
                      just when it shares the words. Choose a provider above to enable
                      search by meaning.</>
                  : <>Nothing chosen — the application owner's own profile is used.</>}
            </span>
          )}
        </label>

        {embSource === 'own_key' && (
          <>
            <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
              Provider
              <select style={inp} value={embProvider}
                onChange={(e) => {
                  setEmbProvider(e.target.value); setEmbModel(''); setEmbDims(0); setEmbTest(null)
                }}>
                <option value="">Select a provider…</option>
                {embProviderOptions.map((id) => (
                  <option key={id} value={id}>{PROVIDER_LABEL[id] || id}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
              Model <span style={{ fontSize: 13, color: '#64748b' }}>(optional)</span>
              <input style={inp} value={embModel} placeholder={embDefaultModel}
                onChange={(e) => { setEmbModel(e.target.value); setEmbDims(0); setEmbTest(null) }} />
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {embDefaultModel
                  ? <>Leave blank for <b>{embDefaultModel}</b>{embDefaultDims ? ` (${embDefaultDims} dimensions)` : ''}.
                      This is an <i>embedding</i> model, never a chat model — a chat model has no
                      embeddings endpoint to call.</>
                  : <>Choose a provider to see its default embedding model.</>}
              </span>
            </label>

            <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
              Endpoint <span style={{ fontSize: 13, color: '#64748b' }}>(optional)</span>
              <input style={inp} value={embBaseUrl}
                placeholder="A self-hosted, OpenAI-compatible embeddings URL"
                onChange={(e) => { setEmbBaseUrl(e.target.value); setEmbTest(null) }} />
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Set this to convert your documents on a server you run, so their text never
                leaves your network. Such an endpoint usually needs no API key.
              </span>
            </label>

            <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
              API key
              <input style={inp} type="password" value={embKey} autoComplete="new-password"
                onChange={(e) => { setEmbKey(e.target.value); setEmbTest(null) }}
                placeholder={view?.embedding?.credential_configured
                  ? '•••••••• stored — type to replace'
                  : 'Paste the embedding provider key'} />
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Stored encrypted and never shown again. Leave blank to keep the stored key.
                This is separate from the assistant's key above — the two are different vendors.
              </span>
            </label>
          </>
        )}

        {embSource === 'platform' && embPlatformAvailable && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Your documents are converted by this instance's platform AI
            {view?.embedding_platform?.provider
              ? ` (${PROVIDER_LABEL[view.embedding_platform.provider] || view.embedding_platform.provider}`
                + `${view.embedding_platform.model ? ` · ${view.embedding_platform.model}` : ''})`
              : ''}
            , and the indexing is charged to <b>your organization's</b> AI credits. Filing a
            long document costs more than asking a question of it.
          </span>
        )}

        {embSource === 'platform' && !embPlatformAvailable && (
          <div style={{ fontSize: 13.5, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px' }}>
            {embIncomplete}
          </div>
        )}

        {view?.embedding?.configured && view?.embedding?.effective_model && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Currently indexing with <b>{view.embedding.effective_model}</b>
            {view.embedding.dimensions ? ` (${view.embedding.dimensions} dimensions)` : ''}.
            Changing the provider or model means re-indexing each repository, so stored vectors
            match what questions are compared against.
          </span>
        )}

        {embTest && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, borderRadius: 8,
            padding: '9px 11px', border: `1px solid ${embTest.ok ? '#bbf7d0' : '#fecaca'}`,
            background: embTest.ok ? '#f0fdf4' : '#fef2f2', color: embTest.ok ? '#166534' : '#b91c1c',
          }}>
            {embTest.ok ? <CheckCircle2 size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              : <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
            <span style={{ wordBreak: 'break-word' }}>
              {embTest.ok
                ? (embTest.message || 'The embedding provider answered — documents can be indexed.')
                : (embTest.error || 'The test failed.')}
              {embTest.used_stored_key && <> {' '}<i>(tested the stored key)</i></>}
            </span>
          </div>
        )}

        {!!embSource && (
          <div>
            <button type="button" style={btn()} onClick={runEmbeddingTest}
              disabled={embTesting || saving || !!embIncomplete}
              title={embIncomplete || 'Convert one test phrase with this provider'}>
              {embTesting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Test embeddings
            </button>
          </div>
        )}
      </div>

      <RuntimeImageSection ownerId={ownerId} projectId={projectId} view={view}
        value={img} onChange={setImg} disabled={saving} inputStyle={inp} buttonStyle={btn()} />

      {err && <div style={{ fontSize: 13.5, color: '#dc2626' }}>{err}</div>}

      {test && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, borderRadius: 8,
          padding: '9px 11px', border: `1px solid ${test.ok ? '#bbf7d0' : '#fecaca'}`,
          background: test.ok ? '#f0fdf4' : '#fef2f2', color: test.ok ? '#166534' : '#b91c1c',
        }}>
          {test.ok ? <CheckCircle2 size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            : <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
          <span style={{ wordBreak: 'break-word' }}>
            {test.ok
              ? (test.message || 'The AI answered — this configuration works.')
              : (test.error || 'The test failed.')}
            {test.used_stored_key && <> {' '}<i>(tested the stored key)</i></>}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={btn(true)} onClick={save}
          disabled={saving || testing}
          title={incomplete || 'Save this AI configuration'}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button type="button" style={btn()} onClick={runTest} disabled={testing || saving || !canTest}
          title={onCredit ? "Check that Twasta's AI answers and your credits cover it"
            : canTest ? 'Send a one-token request to the selected model'
              : 'Choose a provider and enter (or save) an API key first'}>
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Test
        </button>
        {view?.configured && (
          <button type="button" style={{ ...btn(), color: '#dc2626' }} onClick={remove} disabled={saving || testing}>
            <Trash2 size={14} /> Remove
          </button>
        )}
        {view?.updated_at && (
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#94a3b8' }}>
            Last updated {new Date(view.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  )
}

function errText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return e instanceof Error ? e.message : ''
}
